/**
 * /admin/edit/degree/:id  +  /admin/edit/university/:id
 *
 * Renders the RegistryFieldEditor in ADMIN mode. Writes an
 * audit row with `actor_type: "staff"` and respects staff-only
 * registry entries (e.g. World rank) that the uni-portal
 * variant hides.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RegistryFieldEditor from "@/components/uni-portal/RegistryFieldEditor";

function AdminEditShell({ docType, docId }) {
  const { user, loading: authLoading } = useAuth();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = docType === "degrees" ? "degrees" : "universities";
        const { data } = await api.get(`/${path}/${docId}`);
        if (!cancelled) setDoc(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [docType, docId]);

  if (authLoading) {
    return <div className="p-6 text-stone-400 text-sm"><Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Loading…</div>;
  }
  if (!user) {
    return <div className="p-6 text-stone-600">Sign in as admin to view this page.</div>;
  }
  if (err) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-sm text-rose-700">Error: {err}</div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-stone-500 text-sm">
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading…
      </div>
    );
  }

  const title = docType === "degrees"
    ? (doc.official_title || doc.id)
    : (doc.name || doc.id);
  const publicHref = docType === "degrees"
    ? `/degree/${doc.id}` : `/uni/${doc.id}`;

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/admin/edit"
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-900"
          data-testid="admin-edit-page-back"
        >
          <ArrowLeft className="h-3 w-3" /> Search
        </Link>
        <div className="mt-4 flex items-baseline gap-3">
          <ShieldCheck className="h-4 w-4 text-purple-800" />
          <h1 className="font-editorial text-3xl text-stone-900">{title}</h1>
        </div>
        <div className="mt-1 text-[12px] text-stone-500 font-mono">
          {doc.id} ·{" "}
          <Link to={publicHref} className="hover:underline">View public page</Link>
        </div>

        <RegistryFieldEditor
          docType={docType}
          docId={doc.id}
          doc={doc}
          apiPrefix="admin"
        />
      </div>
    </div>
  );
}

export function AdminEditDegreePage() {
  const { id } = useParams();
  return <AdminEditShell docType="degrees" docId={id} />;
}

export function AdminEditUniversityPage() {
  const { id } = useParams();
  return <AdminEditShell docType="universities" docId={id} />;
}
